import type { MessengerChannel, MessengerViewName } from '@widgets/messenger/types'
import { useWidgetLocalStorageValue } from '@widgets/messenger/compositions/useWidgetLocalStorageValue'
import { useWidgetStore } from '@widgets/messenger/stores/useWidgetStore'
import { useWidgetChannels } from '@widgets/messenger/compositions/useWidgetChannels'
import { useChatStore } from '@widgets/messenger/stores/useChatStore'
import { computed, watch } from '@/composition/vue/compositionApi'
import { WebWidgetChannelType } from '@/data/models/domain/messages/settings/webWidget/types'

const channelTypeToViewMap: Record<MessengerChannel['type'], MessengerViewName> = {
  [WebWidgetChannelType.EMAIL]: 'email',
  [WebWidgetChannelType.SMS]: 'sms',
  [WebWidgetChannelType.CALL]: 'call',
  [WebWidgetChannelType.CHAT]: 'chat',
  [WebWidgetChannelType.WHATSAPP]: 'whatsapp',
  [WebWidgetChannelType.FACEBOOK]: 'facebook',
  [WebWidgetChannelType.INSTAGRAM]: 'instagram',
}

export const useWidgetNavigation = () => {
  const { activeSortedChannelTypes } = useWidgetChannels()
  const { uniqueCode, domain, viewNameBeforeMinimize, currentViewName, setViewNameBeforeMinimize, setCurrentViewName } =
    useWidgetStore()
  const { isActiveChat } = useChatStore()

  const lastViewLocalStorage = useWidgetLocalStorageValue<MessengerViewName>(
    {
      uniqueCode: uniqueCode.value,
      domain: domain.value,
      dataKey: 'lastView',
    },
    'minimized',
  )

  const hasOnlyOneActiveChannel = computed(() => activeSortedChannelTypes.value.length === 1)
  const hasOnlyChatChannel = computed(
    () => hasOnlyOneActiveChannel.value && activeSortedChannelTypes.value[0] === WebWidgetChannelType.CHAT,
  )

  const initActiveChatListening = () => {
    watch(
      isActiveChat,
      (value) => {
        if (currentViewName.value === 'minimized') {
          return
        }
        if (value && hasOnlyChatChannel.value) {
          goToActiveChat()
          return
        }
        if (!value && currentViewName.value === 'chat') {
          goToMainView()
        }
      },
      { flush: 'sync' },
    )
  }

  const init = (_hasActiveChat: boolean) => {
    initActiveChatListening()
    const lastViewName = lastViewLocalStorage.get()

    if (lastViewName === 'minimized') {
      goToView(lastViewName)
      return
    }
    if (hasOnlyChatChannel.value && _hasActiveChat) {
      goToView('chat')
      return
    }
    if (lastViewName === 'chat' && !_hasActiveChat) {
      goToMainView()
      return
    }
    if (hasOnlyOneActiveChannel.value && lastViewName === 'welcome') {
      goToMainView()
      return
    }
    goToView(lastViewName)
  }

  const goToView = (viewName: MessengerViewName) => {
    setCurrentViewName(viewName)
    lastViewLocalStorage.set(viewName)
  }

  const minimize = (fromView: MessengerViewName | undefined) => {
    setViewNameBeforeMinimize(fromView)
    goToView('minimized')
  }

  const goToMainView = () => {
    if (hasOnlyOneActiveChannel.value && activeSortedChannelTypes.value[0] !== WebWidgetChannelType.CHAT) {
      goToView(channelTypeToViewMap[activeSortedChannelTypes.value[0]])
    } else {
      goToView('welcome')
    }
  }

  const onWidgetExpand = () => {
    if (hasOnlyChatChannel.value && isActiveChat.value) {
      goToView('chat')
      return
    }
    if (hasOnlyChatChannel.value && !isActiveChat.value) {
      goToMainView()
      return
    }
    if (viewNameBeforeMinimize.value) {
      goToView(viewNameBeforeMinimize.value)
      return
    }
    goToMainView()
  }

  const onWidgetMinimize = () => {
    minimize(currentViewName.value)
  }

  const onClickChannel = (channelType: WebWidgetChannelType) => {
    goToView(channelTypeToViewMap[channelType])
  }

  const onBackFromChannel = (channelType: WebWidgetChannelType) => {
    if (hasOnlyOneActiveChannel.value && channelType !== WebWidgetChannelType.CHAT) {
      minimize(channelTypeToViewMap[channelType])
    } else {
      goToView('welcome')
    }
  }

  const goToViewWithinChannel = (viewName: MessengerViewName) => {
    goToView(viewName)
  }

  const goToActiveChat = () => {
    goToView('chat')
  }

  return {
    currentViewName,
    init,
    minimize,
    goToMainView,
    onWidgetExpand,
    onWidgetMinimize,
    onClickChannel,
    onBackFromChannel,
    goToViewWithinChannel,
  }
}
